// B2C-665 free gift orchestrator.
//
// All eligibility math lives server-side in cart-products-alternate.liquid, which
// renders one hidden .tb-free-gift-state span per rule on every cart morph. This
// module only compares that server-computed state against the static rule config
// serialized by cart-drawer-alternate.liquid (window.TB_FREE_GIFT) and issues the
// matching cart mutation. It never computes collection membership or totals itself,
// never opens the drawer, and never calls any external host.
//
// Re-entrance: a single in-flight flag plus a debounce absorb the cart:update
// bursts documented in BRD-512 (discount flows re-render twice). Our own mutations
// dispatch cart:update with source "free-gift", which this module ignores.

const SOURCE = 'free-gift';
const DEBOUNCE_MS = 200;
const DISMISSED_PREFIX = 'tb-free-gift-dismissed:';
const ADDED_PREFIX = 'tb-free-gift-added:';

if (!window.__tbFreeGift) {
  window.__tbFreeGift = true;

  const rules = Array.isArray(window.TB_FREE_GIFT) ? window.TB_FREE_GIFT : [];

  if (rules.length > 0) {
    // Same id the drawer's cart-items-component registers for the Section
    // Rendering API. Resolved from the DOM so a section rename never desyncs us.
    const sectionId = () => {
      const component = document.querySelector('cart-items-component[data-drawer]');
      return (component && component.dataset.sectionId) || 'cart_drawer';
    };

    let inFlight = false;
    let debounceTimer = null;
    // Rules that returned an add error this page load (gift out of stock, cart
    // validation). Never retried until the next page load.
    const failedRules = new Set();

    const session = {
      get(key) {
        try {
          return sessionStorage.getItem(key);
        } catch (error) {
          return null;
        }
      },
      set(key, value) {
        try {
          sessionStorage.setItem(key, value);
        } catch (error) {
          /* private mode: degrade to no memory */
        }
      },
      remove(key) {
        try {
          sessionStorage.removeItem(key);
        } catch (error) {
          /* noop */
        }
      },
    };

    const readState = () => {
      const state = new Map();
      for (const span of document.querySelectorAll('.tb-free-gift-state')) {
        state.set(span.dataset.ruleId, {
          eligibleTotal: parseInt(span.dataset.eligibleTotal, 10) || 0,
          eligibleCount: parseInt(span.dataset.eligibleCount, 10) || 0,
          giftKey: span.dataset.giftKey || '',
          giftQty: parseInt(span.dataset.giftQty, 10) || 0,
        });
      }
      return state;
    };

    const isWithinWindow = (rule) => {
      const now = Math.floor(Date.now() / 1000);
      if (rule.startTs > 0 && now < rule.startTs) return false;
      if (rule.endTs > 0 && now > rule.endTs) return false;
      return true;
    };

    const dispatchCartUpdate = (cartJson, sections) => {
      let itemCount = 0;
      const sectionHtml = sections && sections[sectionId()];
      if (sectionHtml) {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const countRef = doc.querySelector('[ref="cartItemCount"]');
        itemCount = parseInt(countRef && countRef.textContent, 10) || 0;
      }
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartJson,
            sourceId: SOURCE,
            data: {
              source: SOURCE,
              itemCount,
              sections,
            },
          },
        })
      );
    };

    const mutateCart = async (path, body) => {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: sectionId(),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        throw new Error(`free-gift cart mutation failed (${response.status})`);
      }
      const json = await response.json();
      dispatchCartUpdate(json, json.sections);
    };

    const addGift = (rule) =>
      mutateCart('/cart/add.js', {
        items: [
          {
            id: rule.variantId,
            quantity: 1,
            properties: { _priority_free: 'true' },
          },
        ],
      });

    const removeGift = (giftKey) => mutateCart('/cart/change.js', { id: giftKey, quantity: 0 });

    const evaluate = async () => {
      if (inFlight) return;

      const state = readState();

      for (const rule of rules) {
        const ruleState = state.get(rule.id);
        if (!ruleState) continue;

        const eligible =
          isWithinWindow(rule) &&
          ruleState.eligibleCount > 0 &&
          ruleState.eligibleTotal >= rule.minCents;
        const hasGift = ruleState.giftKey !== '';
        const dismissed = session.get(DISMISSED_PREFIX + rule.id) === '1';
        const wasAdded = session.get(ADDED_PREFIX + rule.id) === '1';

        try {
          if (eligible && !hasGift) {
            if (wasAdded && !dismissed) {
              // We added it earlier and it vanished while still eligible: the
              // customer removed it on purpose. Respect that for the session.
              session.set(DISMISSED_PREFIX + rule.id, '1');
              continue;
            }
            if (dismissed || failedRules.has(rule.id) || !rule.available) continue;
            inFlight = true;
            await addGift(rule);
            session.set(ADDED_PREFIX + rule.id, '1');
            return;
          }

          if (!eligible && hasGift) {
            inFlight = true;
            await removeGift(ruleState.giftKey);
            session.remove(ADDED_PREFIX + rule.id);
            return;
          }

          if (!eligible) {
            // Conditions dropped: forget history so the gift comes back if the
            // customer requalifies later in the session.
            session.remove(ADDED_PREFIX + rule.id);
            session.remove(DISMISSED_PREFIX + rule.id);
          }
        } catch (error) {
          failedRules.add(rule.id);
          console.error('[free-gift]', error);
          return;
        } finally {
          if (inFlight) {
            inFlight = false;
            // The mutation dispatched its own cart:update; the debounced listener
            // below re-evaluates once, letting a second rule settle if needed.
          }
        }
      }
    };

    const scheduleEvaluate = (event) => {
      if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) {
        // Our own dispatch: still re-evaluate (another rule may now apply) but
        // let the standard debounce absorb the burst.
      }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(evaluate, DEBOUNCE_MS);
    };

    document.addEventListener('cart:update', scheduleEvaluate);
    document.addEventListener('discount:update', scheduleEvaluate);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => scheduleEvaluate());
    } else {
      scheduleEvaluate();
    }
  }
}
